import React, { useState, useCallback } from 'react';
import DatasetPicker from '../components/dashboard-builder/DatasetPicker';
import SavedDashboardsList from '../components/dashboard-builder/SavedDashboardsList';
import DashboardCanvas from '../components/dashboard-builder/DashboardCanvas';
import BuilderChat from '../components/dashboard-builder/BuilderChat';
import { dashboardBuilderService } from '../services/dashboardBuilderService';
import type { DashboardWidget, DashboardFilter, BuilderChatMessage, SavedDashboard, SuggestionGroup, WidgetType } from '../types/dashboard.types';
import type { ChartPanelChartType } from '../components/chat/ChartPanel';
import type { Layout } from 'react-grid-layout';

type ViewState = 'picker' | 'saved' | 'builder';

/** Grouped suggestion chips for a new empty dashboard */
function getGroupedSuggestions(datasetNames: string[]): SuggestionGroup[] {
  const ds = datasetNames.map((n) => n.toLowerCase());
  const groups: SuggestionGroup[] = [];

  // Auto-build suggestions
  const autoSuggestions: string[] = [];
  if (ds.some((d) => d.includes('finance'))) {
    autoSuggestions.push('Build me a financial overview dashboard');
  }
  if (ds.some((d) => d.includes('abs') || d.includes('census'))) {
    autoSuggestions.push('Build me a demographics overview dashboard');
  }
  autoSuggestions.push('Build me a complete dashboard with KPIs, charts and a table');

  groups.push({
    category: 'Auto-Build Dashboard',
    icon: 'BIDashboard',
    suggestions: autoSuggestions,
  });

  // Always show KPIs
  groups.push({
    category: 'Key Performance Indicators',
    icon: 'SpeedHigh',
    suggestions: [
      'Add a KPI showing the total number of records',
      'Show the total dollar amount as a KPI card',
    ],
  });

  // Financial (if finance dataset)
  if (ds.some((d) => d.includes('finance'))) {
    groups.push({
      category: 'Financial Analysis',
      icon: 'Money',
      suggestions: [
        'Add a bar chart of budget vs actuals by cost centre',
        'Show a donut chart of spend by top 10 suppliers',
        'Add a line chart of monthly expenditure trend',
      ],
    });
  }

  // Census / Demographics (if ABS dataset)
  if (ds.some((d) => d.includes('abs') || d.includes('census'))) {
    groups.push({
      category: 'Census & Demographics',
      icon: 'People',
      suggestions: [
        'Add a bar chart of total population by suburb for 2021',
        'Show median household weekly income by SA2 area as a bar chart',
        'Add a KPI showing the total population across all suburbs',
      ],
    });
  }

  // Always show chart-related
  groups.push({
    category: 'Charts & Comparisons',
    icon: 'BarChart4',
    suggestions: [
      'Add a bar chart comparing the top 10 values',
      'Show a trend over time as a line chart',
      'Break down the totals as a donut chart',
    ],
  });

  return groups;
}

/** Flatten grouped suggestions into a simple string[] for the chat panel */
function flattenSuggestions(groups: SuggestionGroup[]): string[] {
  return groups.flatMap((g) => g.suggestions).slice(0, 6);
}

/** Compute grid position for a new widget by packing into available space */
function computeGridPosition(
  existingWidgets: DashboardWidget[],
  widgetW: number,
  widgetH: number,
): { gridX: number; gridY: number } {
  const COLS = 12;
  const colBottom = new Array(COLS).fill(0);

  for (const w of existingWidgets) {
    const x = w.gridX ?? 0;
    const y = w.gridY ?? 0;
    const gw = w.gridW ?? (w.size === 'small' ? 4 : w.size === 'large' ? 12 : 6);
    const gh = w.gridH ?? (w.type === 'kpi' || w.type === 'number' ? 2 : w.type === 'table' ? 5 : 4);
    for (let c = x; c < Math.min(x + gw, COLS); c++) {
      colBottom[c] = Math.max(colBottom[c], y + gh);
    }
  }

  let bestX = 0;
  let bestY = Infinity;
  for (let x = 0; x <= COLS - widgetW; x++) {
    let rowY = 0;
    for (let c = x; c < x + widgetW; c++) {
      rowY = Math.max(rowY, colBottom[c]);
    }
    if (rowY < bestY) {
      bestY = rowY;
      bestX = x;
    }
  }

  return { gridX: bestX, gridY: bestY };
}

function sizeToGridW(size: string): number {
  if (size === 'small') return 4;
  if (size === 'large') return 12;
  return 6;
}

function defaultGridH(type: string): number {
  if (type === 'kpi' || type === 'number') return 2;
  if (type === 'table') return 5;
  return 4;
}

/** Strip verbs & suffixes from a user query to produce a clean widget title */
function deriveFrontendTitle(query: string): string {
  let t = query.trim();
  t = t.replace(/^(add|show|create|display|give\s+me\s+a?|get|what(?:'s|\s+is)?\s+(?:the\s+)?)\s*/i, '');
  t = t.replace(/\s+(as\s+a\s+widget|as\s+a\s+kpi|as\s+a\s+chart|as\s+a\s+table|as\s+a\s+card|as\s+a\s+number\s+card|as\s+a\s+(?:bar|line|pie|donut|area)\s+chart)\s*$/i, '');
  t = t.replace(/\b\w/g, (c) => c.toUpperCase());
  return t.slice(0, 60) || 'Widget';
}

export default function DashboardBuilder() {
  // View state
  const [view, setView] = useState<ViewState>('picker');

  // Dashboard state
  const [dashboardId, setDashboardId] = useState<string | null>(null);
  const [dashboardName, setDashboardName] = useState('Untitled Dashboard');
  const [datasetIds, setDatasetIds] = useState<string[]>([]);
  const [datasetNames, setDatasetNames] = useState<string[]>([]);
  const [widgets, setWidgets] = useState<DashboardWidget[]>([]);
  const [chatMessages, setChatMessages] = useState<BuilderChatMessage[]>([]);
  const [filters, setFilters] = useState<DashboardFilter[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestionGroups, setSuggestionGroups] = useState<SuggestionGroup[]>([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [chatCollapsed, setChatCollapsed] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateOffered, setTemplateOffered] = useState(false);

  // ── Load finance template ──
  const handleLoadTemplate = useCallback(async () => {
    setTemplateLoading(true);
    setTemplateOffered(false);
    try {
      const tpl = await dashboardBuilderService.getTemplate('finance');

      // Build widgets with grid positions
      const newWidgets: DashboardWidget[] = [];
      // Process by tab so each tab's layout starts from row 0
      const tabGroups = new Map<string, typeof tpl.widgets>();
      for (const w of tpl.widgets) {
        const tab = w.tabId || '__none__';
        if (!tabGroups.has(tab)) tabGroups.set(tab, []);
        tabGroups.get(tab)!.push(w);
      }

      for (const [, tabWidgets] of tabGroups) {
        const tabContext: DashboardWidget[] = [];
        for (const w of tabWidgets) {
          const widgetType = w.type || 'chart';
          const widgetSize = w.size || 'medium';
          const gridW = sizeToGridW(widgetSize);
          const gridH = defaultGridH(widgetType);
          const { gridX, gridY } = computeGridPosition(tabContext, gridW, gridH);

          const widget: DashboardWidget = {
            id: w.id || `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: widgetType,
            title: w.title,
            position: newWidgets.length,
            size: widgetSize,
            chartType: w.chartType,
            chartData: w.chartData,
            kpiValue: w.kpiValue,
            kpiLabel: w.kpiLabel,
            kpiIcon: w.kpiIcon,
            kpiTrend: w.kpiTrend,
            colorScheme: w.colorScheme,
            originalSql: w.originalSql,
            gridX,
            gridY,
            gridW,
            gridH,
            tabId: w.tabId,
          };
          newWidgets.push(widget);
          tabContext.push(widget);
        }
      }

      setWidgets(newWidgets);
      setDashboardName('Finance & Contract Expenditure');

      // Load template filters
      if (tpl.filters && tpl.filters.length > 0) {
        setFilters(tpl.filters.map((f: any) => ({
          id: f.id,
          field: f.field,
          label: f.label,
          values: f.values || [],
          selectedValue: '',
        })));
      }

      // Add system message to chat
      const sysMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-tpl`,
        role: 'assistant',
        content: `Finance Overview template loaded with ${newWidgets.length} widgets across 4 tabs. Use the tabs above the dashboard to navigate between Budget & Expenditure, Cost Centres, Projects, and Purchase Orders.`,
        timestamp: new Date().toISOString(),
        suggestions: ['Add a filter for year', 'Show budget vs actuals by department', 'Add a KPI for total POs billed'],
      };
      setChatMessages((prev) => [...prev, sysMsg]);
      setSuggestions(['Add a filter for year', 'Show budget vs actuals by department', 'Add a KPI for total POs billed']);
    } catch (err) {
      console.error('Failed to load template:', err);
      const errMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-terr`,
        role: 'assistant',
        content: 'Failed to load the finance template. You can still build widgets manually using the chat.',
        timestamp: new Date().toISOString(),
      };
      setChatMessages((prev) => [...prev, errMsg]);
    } finally {
      setTemplateLoading(false);
    }
  }, []);

  // ── Start building from dataset picker ──
  const handleStart = useCallback(async (ids: string[], names: string[]) => {
    setDatasetIds(ids);
    setDatasetNames(names);
    const groups = getGroupedSuggestions(names);
    setSuggestionGroups(groups);
    setSuggestions(flattenSuggestions(groups));

    // Check if finance dataset is selected → offer template
    const isFinance = names.some((n) => n.toLowerCase().includes('finance'));
    setTemplateOffered(isFinance);

    // Create a new dashboard in the backend
    try {
      const dashboard = await dashboardBuilderService.createDashboard('Untitled Dashboard', ids);
      setDashboardId(dashboard.id);
    } catch (err) {
      console.error('Failed to create dashboard:', err);
      // Continue without persistence
    }

    setView('builder');
  }, []);

  // ── Load a saved dashboard ──
  const handleLoadSaved = useCallback(async (id: string) => {
    try {
      const dashboard: SavedDashboard = await dashboardBuilderService.getDashboard(id);
      setDashboardId(dashboard.id);
      setDashboardName(dashboard.name);
      setDatasetIds(dashboard.datasetIds);
      setDatasetNames(dashboard.datasetIds); // Use IDs as names if no mapping
      setWidgets(dashboard.widgets || []);
      setChatMessages(dashboard.chatHistory || []);
      const groups = getGroupedSuggestions(dashboard.datasetIds);
      setSuggestionGroups(groups);
      setSuggestions(flattenSuggestions(groups));
      setView('builder');
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    }
  }, []);

  // ── Save dashboard ──
  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      // If the initial create failed (or never ran), create on the fly so save always persists
      let id = dashboardId;
      if (!id) {
        const created = await dashboardBuilderService.createDashboard(dashboardName, datasetIds);
        id = created.id;
        setDashboardId(id);
      }
      await dashboardBuilderService.updateDashboard(id, {
        name: dashboardName,
        widgets,
        chatHistory: chatMessages,
      });
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save dashboard:', err);
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        || (err as { message?: string })?.message
        || 'Unknown error';
      alert(`Failed to save dashboard: ${msg}`);
    } finally {
      setSaving(false);
    }
  }, [dashboardId, dashboardName, datasetIds, widgets, chatMessages]);

  // ── Chat: send a message ──
  const handleSend = useCallback(async (text: string) => {
    // Add user message
    const userMsg: BuilderChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setChatMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const response = await dashboardBuilderService.query({
        query: text,
        datasetIds,
        existingWidgets: widgets,
      });

      // ── Handle action: add / modify / remove ──
      let widgetAction: BuilderChatMessage['widgetAction'] | undefined;

      if (response.action === 'add_widget' && response.widgets && response.widgets.length > 0) {
        // ── ADD NEW WIDGETS ──
        // Sort: number/kpi first so they pack on top row together
        const typeOrder: Record<string, number> = { number: 0, kpi: 1, chart: 2, table: 3 };
        const sortedResponseWidgets = [...response.widgets].sort(
          (a, b) => (typeOrder[a.type || 'chart'] ?? 2) - (typeOrder[b.type || 'chart'] ?? 2)
        );

        const newWidgets: DashboardWidget[] = [];
        // Track cumulative layout including existing + already-placed new widgets
        const layoutContext = [...widgets];

        for (const w of sortedResponseWidgets) {
          // Prefer per-widget chartData (multi-widget), fall back to global
          const widgetChartData = w.chartData || response.chartData || undefined;

          const widgetType = w.type || 'chart';
          // Number/KPI default to small, tables to large, charts to medium
          const widgetSize = w.size || (widgetType === 'number' || widgetType === 'kpi' ? 'small' : widgetType === 'table' ? 'large' : 'medium');
          const gridW = sizeToGridW(widgetSize);
          const gridH = defaultGridH(widgetType);

          // Compute grid position based on existing + already-placed widgets
          const { gridX, gridY } = computeGridPosition(layoutContext, gridW, gridH);

          const widget: DashboardWidget = {
            id: `widget-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            type: widgetType,
            title: w.title || deriveFrontendTitle(text),
            position: widgets.length + newWidgets.length,
            size: widgetSize,
            chartType: w.chartType,
            chartData: widgetChartData,
            kpiValue: w.kpiValue,
            kpiLabel: w.kpiLabel,
            kpiIcon: w.kpiIcon,
            kpiTrend: w.kpiTrend,
            query: text,
            colorScheme: w.colorScheme,
            originalSql: w.originalSql,
            gridX,
            gridY,
            gridW,
            gridH,
            tabId: w.tabId,
          };

          // If it's a KPI or number and we got chart data with a single row, extract the value
          if ((widget.type === 'kpi' || widget.type === 'number') && widgetChartData && widgetChartData.rows && widgetChartData.rows.length > 0 && widgetChartData.fields) {
            const row = widgetChartData.rows[0];
            const fields = widgetChartData.fields;
            const numField = fields.find((f: { name: string }) => {
              const val = row[f.name];
              return typeof val === 'number' || !isNaN(Number(String(val).replace(/[$,]/g, '')));
            });
            if (numField && !widget.kpiValue) {
              widget.kpiValue = row[numField.name];
            }
          }

          newWidgets.push(widget);
          layoutContext.push(widget); // Track for next widget's position calculation
        }
        if (newWidgets.length > 0) {
          setWidgets((prev) => [...prev, ...newWidgets]);
          widgetAction = { action: 'add_widget', widget: newWidgets[0] };
        }

      } else if (response.action === 'modify_widget' && response.widgets && response.widgets.length > 0) {
        // ── MODIFY EXISTING WIDGETS ──
        for (const w of response.widgets) {
          // Match by ID from AI, or fall back to fuzzy title match
          const matchId = w.id;
          setWidgets((prev) => prev.map((existing) => {
            const isMatch = matchId
              ? existing.id === matchId
              : existing.title.toLowerCase() === (w.title || '').toLowerCase();
            if (!isMatch) return existing;
            // Merge only the properties the AI returned
            return {
              ...existing,
              ...(w.title ? { title: w.title } : {}),
              ...(w.size ? { size: w.size } : {}),
              ...(w.chartType ? { chartType: w.chartType } : {}),
              ...(w.colorScheme ? { colorScheme: w.colorScheme } : {}),
              ...(w.kpiLabel ? { kpiLabel: w.kpiLabel } : {}),
              ...(w.kpiIcon ? { kpiIcon: w.kpiIcon } : {}),
              ...(w.kpiTrend ? { kpiTrend: w.kpiTrend } : {}),
              ...(w.type ? { type: w.type } : {}),
              // If the AI returned new chart data (re-queried), apply it
              ...(response.chartData ? { chartData: response.chartData } : {}),
            };
          }));
          widgetAction = { action: 'modify_widget', widgetId: matchId || w.title };
        }

      } else if (response.action === 'remove_widget' && response.widgets && response.widgets.length > 0) {
        // ── REMOVE WIDGETS ──
        for (const w of response.widgets) {
          const matchId = w.id;
          setWidgets((prev) => prev.filter((existing) => {
            if (matchId) return existing.id !== matchId;
            return existing.title.toLowerCase() !== (w.title || '').toLowerCase();
          }));
          widgetAction = { action: 'remove_widget', widgetId: matchId || w.title };
        }
      }

      // ── Handle add_filter ──
      // Surface failures to the user instead of swallowing them — a filter that
      // can't be built (e.g. its values can't be read from the connected
      // dataset) previously failed silently, so "add a filter" looked broken.
      let filterNote: string | null = null;
      if (response.action === 'add_filter' && response.filter) {
        const { field, label, sql } = response.filter;
        try {
          const values = await dashboardBuilderService.getFilterValues(sql, datasetIds);
          if (!values || values.length === 0) {
            filterNote = `I couldn't add a "${label}" filter — no values came back for that field in the connected data.`;
          } else {
            const newFilter: DashboardFilter = {
              id: `filter-${Date.now()}`,
              field,
              label,
              values,
              selectedValue: '',
            };
            setFilters((prev) => [...prev, newFilter]);
          }
        } catch (err2) {
          console.error('Failed to load filter values:', err2);
          filterNote = `I couldn't add the "${label}" filter — its values couldn't be read from the connected dataset. Try naming a field that appears in one of your current widgets.`;
        }
      }

      // Add assistant message (append any filter note so a failed filter is visible)
      const baseMessage = response.message || "Here's what I've done.";
      const assistantMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-a`,
        role: 'assistant',
        content: filterNote ? `${baseMessage} ${filterNote}` : baseMessage,
        timestamp: new Date().toISOString(),
        suggestions: response.suggestions,
        widgetAction,
      };
      setChatMessages((prev) => [...prev, assistantMsg]);
      setSuggestions(response.suggestions || []);

    } catch (err) {
      console.error('Dashboard builder query failed:', err);
      const errMsg: BuilderChatMessage = {
        id: `msg-${Date.now()}-e`,
        role: 'assistant',
        content: "Sorry, I couldn't process that request. Could you try rephrasing?",
        timestamp: new Date().toISOString(),
        suggestions: ['Try a simpler query', 'Ask what data is available'],
      };
      setChatMessages((prev) => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  }, [datasetIds, widgets]);

  // ── Close / New Dashboard ──
  const resetDashboard = useCallback(() => {
    setDashboardId(null);
    setDashboardName('Untitled Dashboard');
    setDatasetIds([]);
    setDatasetNames([]);
    setWidgets([]);
    setFilters([]);
    setChatMessages([]);
    setSuggestions([]);
    setSuggestionGroups([]);
    setTemplateOffered(false);
    setTemplateLoading(false);
    setView('picker');
  }, []);

  const handleNewDashboard = useCallback(() => {
    resetDashboard();
  }, [resetDashboard]);

  const handleCloseDashboard = useCallback(() => {
    resetDashboard();
  }, [resetDashboard]);

  const handleClearChat = useCallback(() => {
    setChatMessages([]);
    const groups = getGroupedSuggestions(datasetNames);
    setSuggestionGroups(groups);
    setSuggestions(flattenSuggestions(groups));
  }, [datasetNames]);

  // ── Export PDF ──
  const handleExportPdf = useCallback(async () => {
    setExporting(true);
    try {
      const { exportDashboardToPdf } = await import('../utils/exportDashboardToPdf');
      // Collect chart widget DOM elements
      const chartElements = new Map<string, HTMLElement>();
      for (const w of widgets) {
        if (w.type === 'chart') {
          const el = document.querySelector(`[data-widget-id="${w.id}"]`) as HTMLElement | null;
          if (el) chartElements.set(w.id, el);
        }
      }
      await exportDashboardToPdf({
        dashboardName,
        widgets,
        datasetNames,
        chartElements,
      });
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  }, [dashboardName, widgets, datasetNames]);

  // ── Widget actions ──
  const handleRemoveWidget = useCallback((id: string) => {
    setWidgets((prev) => prev.filter((w) => w.id !== id));
  }, []);

  const handleResizeWidget = useCallback((id: string, size: 'small' | 'medium' | 'large') => {
    const gridW = size === 'small' ? 4 : size === 'large' ? 12 : 6;
    setWidgets((prev) => prev.map((w) => {
      if (w.id !== id) return w;
      return { ...w, size, gridW };
    }));
  }, []);

  const handleChangeChartType = useCallback((id: string, chartType: ChartPanelChartType) => {
    setWidgets((prev) => prev.map((w) => w.id === id ? { ...w, chartType } : w));
  }, []);

  const handleUpdateLegendLabels = useCallback((id: string, legendLabels: Record<string, string>) => {
    setWidgets((prev) => prev.map((w) => w.id === id ? { ...w, legendLabels } : w));
  }, []);

  // ── Filter handlers ──
  const handleFilterChange = useCallback(async (filterId: string, value: string) => {
    // Update filter state
    setFilters((prev) => prev.map((f) => f.id === filterId ? { ...f, selectedValue: value } : f));

    // Compute active filters from current state + new change (avoids stale closure)
    setFilters((currentFilters) => {
      const activeFilters = currentFilters
        .map((f) => f.id === filterId ? { ...f, selectedValue: value } : f)
        .filter((f) => f.selectedValue);
      const filterParams = activeFilters.map((f) => ({ field: f.field, value: f.selectedValue }));

      // Re-query widgets OUTSIDE state updater — read current widgets then fire async calls
      setWidgets((currentWidgets) => {
        const widgetsWithSql = currentWidgets.filter((w) => w.originalSql);
        // Fire async re-queries outside of the state updater
        for (const w of widgetsWithSql) {
          dashboardBuilderService.filterQuery(w.originalSql!, filterParams, datasetIds)
            .then((result) => {
              if (result?.chartData?.rows && result?.chartData?.fields) {
                setWidgets((prev) => prev.map((pw) => pw.id === w.id ? { ...pw, chartData: result.chartData } : pw));
              }
            })
            .catch((err) => console.error(`Filter re-query failed for ${w.title}:`, err));
        }
        return currentWidgets; // No immediate change
      });

      return currentFilters; // Don't modify filters in this read
    });
  }, [datasetIds]);

  const handleRemoveFilter = useCallback((filterId: string) => {
    setFilters((prev) => prev.filter((f) => f.id !== filterId));
    // Re-query widgets with remaining filters
    setFilters((currentFilters) => {
      const remaining = currentFilters.filter((f) => f.id !== filterId && f.selectedValue);
      const filterParams = remaining.map((f) => ({ field: f.field, value: f.selectedValue }));
      setWidgets((currentWidgets) => {
        for (const w of currentWidgets.filter((w) => w.originalSql)) {
          dashboardBuilderService.filterQuery(w.originalSql!, filterParams, datasetIds)
            .then((result) => {
              if (result?.chartData?.rows && result?.chartData?.fields) {
                setWidgets((prev) => prev.map((pw) => pw.id === w.id ? { ...pw, chartData: result.chartData } : pw));
              }
            })
            .catch((err) => console.error(`Filter re-query failed for ${w.title}:`, err));
        }
        return currentWidgets;
      });
      return currentFilters;
    });
  }, [datasetIds]);

  const handleLayoutChange = useCallback((layout: Layout) => {
    setWidgets((prev) => prev.map((w) => {
      const item = layout.find((l) => l.i === w.id);
      if (!item) return w;
      return { ...w, gridX: item.x, gridY: item.y, gridW: item.w, gridH: item.h };
    }));
  }, []);

  const handleChangeWidgetType = useCallback((id: string, newType: WidgetType) => {
    setWidgets((prev) => prev.map((w) => {
      if (w.id !== id) return w;
      if (newType === 'kpi' || newType === 'number') {
        // Extract first numeric value from chartData for KPI/number
        let kpiValue = w.kpiValue;
        if (!kpiValue && w.chartData && w.chartData.rows.length > 0) {
          const row = w.chartData.rows[0];
          const numField = (w.chartData.fields || []).find((f) => {
            const val = row[f.name];
            return typeof val === 'number' || !isNaN(Number(String(val).replace(/[$,]/g, '')));
          });
          if (numField) kpiValue = row[numField.name];
        }
        if (newType === 'number') {
          return { ...w, type: 'number', size: 'small', gridW: 4, gridH: 2, kpiValue };
        }
        return { ...w, type: 'kpi', size: 'small', gridW: 4, gridH: 2, kpiLabel: w.kpiLabel || w.title, kpiValue };
      }
      if (newType === 'table') {
        return { ...w, type: 'table', size: 'large', gridW: 12, gridH: 5 };
      }
      // chart
      const newSize = w.size === 'small' ? 'medium' : w.size;
      return { ...w, type: 'chart', chartType: w.chartType || 'bar', size: newSize, gridW: newSize === 'large' ? 12 : 6, gridH: 4 };
    }));
  }, []);

  // ── Render ──
  if (view === 'saved') {
    return (
      <SavedDashboardsList
        onOpen={handleLoadSaved}
        onBack={() => setView('picker')}
      />
    );
  }

  if (view === 'picker') {
    return (
      <DatasetPicker
        onStart={handleStart}
        onLoadSaved={() => setView('saved')}
      />
    );
  }

  // Builder view
  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 52px)',
      overflow: 'hidden',
    }}>
      {/* Template offer banner */}
      {templateOffered && widgets.length === 0 && (
        <div style={{
          position: 'absolute',
          top: 64,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          background: '#FFFFFF',
          border: '1px solid #2e873d',
          borderRadius: 8,
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <span style={{ fontSize: 13, color: '#232323', fontWeight: 500 }}>
            Use the Finance Overview template?
          </span>
          <button
            onClick={handleLoadTemplate}
            disabled={templateLoading}
            style={{
              padding: '6px 16px',
              borderRadius: 4,
              background: '#2e873d',
              color: '#FFFFFF',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: templateLoading ? 'default' : 'pointer',
              opacity: templateLoading ? 0.6 : 1,
            }}
          >
            {templateLoading ? 'Loading...' : 'Load Template'}
          </button>
          <button
            onClick={() => setTemplateOffered(false)}
            style={{
              padding: '6px 12px',
              borderRadius: 4,
              background: 'transparent',
              color: '#747474',
              border: '1px solid #E1E1E1',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            No thanks
          </button>
        </div>
      )}
      <DashboardCanvas
        dashboardName={dashboardName}
        onNameChange={setDashboardName}
        widgets={widgets}
        datasetNames={datasetNames}
        filters={filters}
        onFilterChange={handleFilterChange}
        onRemoveFilter={handleRemoveFilter}
        onRemoveWidget={handleRemoveWidget}
        onResizeWidget={handleResizeWidget}
        onChangeChartType={handleChangeChartType}
        onChangeWidgetType={handleChangeWidgetType}
        onUpdateLegendLabels={handleUpdateLegendLabels}
        onLayoutChange={handleLayoutChange}
        onSave={handleSave}
        saving={saving}
        justSaved={justSaved}
        onExportPdf={handleExportPdf}
        exporting={exporting}
        onClose={handleCloseDashboard}
        onNewDashboard={handleNewDashboard}
        suggestionChips={widgets.length === 0 ? suggestions : undefined}
        suggestionGroups={widgets.length === 0 ? suggestionGroups : undefined}
        onSuggestionClick={handleSend}
      />
      <BuilderChat
        messages={chatMessages}
        onSend={handleSend}
        loading={loading}
        datasetNames={datasetNames}
        suggestions={suggestions}
        onSuggestionClick={handleSend}
        onClearChat={handleClearChat}
        isCollapsed={chatCollapsed}
        onToggleCollapse={() => setChatCollapsed(v => !v)}
      />
    </div>
  );
}
